import {isArchivedTag} from '../../../../../../helpers/models/tagHelpers';
import {RuleBundleModel} from '../../../../../../models/api/ruleBundleModel';
import {PartialRuleModelStrict, RuleModelStrict} from '../../../../../../models/api/ruleModel';
import {decorateWithClientId} from '../../../../../editors/commons/clientIdModel';
import {ActivityTypesEnum, BusinessHoursParametersEnum} from '../../../../../editors/commons/paramModel';
import {buildCompoundConditions} from '../../../../../editors/conditions/compoundConditionModel';
import {buildRuleAction} from '../../../../ruleActionHelpers';
import {RuleAction, RuleActionTypesEnum} from '../../../../ruleActionModel';
import {buildRuleCondition} from '../../../../ruleConditionHelpers';
import {RuleConditionTypesEnum, UnrepliedAfterCondition} from '../../../../ruleConditionModel';
import {initialSlaState} from '../../../../slaRuleHelpers';
import {SlaEditorSpecificState, SlaTagsModels} from './slaEditorState';

interface SlaEditorInitialState {
  rule: PartialRuleModelStrict | undefined;
  slaSpecificState: SlaEditorSpecificState;
}

export function createInitialSlaState(
  initialBundle: RuleBundleModel | undefined,
  slaTags: SlaTagsModels,
): SlaEditorInitialState {
  if (initialBundle) {
    return fromBundle(initialBundle, slaTags);
  }
  return fromDefault(slaTags);
}

function fromDefault(slaTags: SlaTagsModels): SlaEditorInitialState {
  const breachTagId = !isArchivedTag(slaTags.breach) ? slaTags.breach.id : undefined;
  const warningTagId = !isArchivedTag(slaTags.warning) ? slaTags.warning.id : undefined;

  return {
    rule: undefined,
    slaSpecificState: {
      unreplied: {
        breach: getDefaultBreachCondition(),
      },
      actions: {
        breach: getDefaultActions(),
        warning: getDefaultActions(),
      },
      tags: {breach: breachTagId, warning: warningTagId},
    },
  };
}

function fromBundle(bundle: RuleBundleModel, slaTags: SlaTagsModels): SlaEditorInitialState {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const slaRules = bundle.rules as ReadonlyArray<RuleModelStrict>;

  const initialState = initialSlaState({slaRules, recipeName: bundle.name});

  const breachActions = initialState.actions.breach;
  const warningActions = initialState.actions.warning;

  const delayBreach = buildRuleCondition(RuleConditionTypesEnum.UNREPLIED_AFTER, {
    ...initialState.timeLimits.breach,
    activity_type: ActivityTypesEnum.INBOUND,
  });

  const delayWarning =
    initialState.timeLimits.warning &&
    buildRuleCondition(RuleConditionTypesEnum.UNREPLIED_AFTER, {
      ...initialState.timeLimits.warning,
      activity_type: ActivityTypesEnum.INBOUND,
    });

  // Try to use the bundle tag if there is one. If it's not available, use the default one (only if it's not archived).
  const breachTagId =
    initialState.tags.breach ?? (!isArchivedTag(slaTags.breach) ? slaTags.breach.id : undefined);
  const warningTagId =
    initialState.tags.warning ?? (!isArchivedTag(slaTags.warning) ? slaTags.warning.id : undefined);

  return {
    rule: {
      id: bundle.id,
      active: initialState.active,
      expressions: [],
      name: bundle.name,
      compoundConditions: buildCompoundConditions(initialState.conditions),
      bundle,
      namespace: bundle.namespace,
    },
    slaSpecificState: {
      unreplied: {breach: delayBreach, warning: delayWarning},
      actions: {breach: breachActions, warning: warningActions},
      tags: {
        breach: breachTagId,
        warning: warningTagId,
      },
      initialRules: {
        ...initialState.initialRules,
        bundle,
      },
    },
  };
}

function getDefaultBreachCondition(): UnrepliedAfterCondition {
  return buildRuleCondition(RuleConditionTypesEnum.UNREPLIED_AFTER, {
    business_hours: BusinessHoursParametersEnum.REGARDLESS_BH,
    activity_type: ActivityTypesEnum.INBOUND,
  });
}

function getDefaultActions() {
  const actions: ReadonlyArray<RuleAction> = [
    buildRuleAction(RuleActionTypesEnum.NOTIFY_TEAMMATES, {teammates: []}),
    buildRuleAction(RuleActionTypesEnum.NOTIFY_ASSIGNEE, {}),
  ];

  return actions.map(decorateWithClientId);
}
